import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, RefreshCw, ShieldCheck, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import KenbrightLogo from '../assets/images/KenbrightLogo';

const PASSWORD_REQUIREMENTS = [
    { id: 'length', label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { id: 'uppercase', label: 'One uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { id: 'lowercase', label: 'One lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { id: 'number', label: 'One number', test: (pwd) => /[0-9]/.test(pwd) },
    { id: 'special', label: 'One special character (@#$%^&+=!)', test: (pwd) => /[@#$%^&+=!]/.test(pwd) },
];

const ChangePasswordPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    const email = location.state?.email;
    const tempPassword = location.state?.tempPassword;

    const [currentPassword, setCurrentPassword] = useState(tempPassword || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Redirect to login if no state
    useEffect(() => {
        if (!email) {
            navigate('/login', { replace: true });
        }
    }, [email, navigate]);

    const passwordStrength = PASSWORD_REQUIREMENTS.map(req => ({
        ...req,
        passed: req.test(newPassword)
    }));

    const allRequirementsMet = passwordStrength.every(req => req.passed);
    const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
    const isFormValid = currentPassword && allRequirementsMet && passwordsMatch;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid) return;

        if (newPassword === currentPassword) {
            setError('New password must be different from current password');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.changePassword(currentPassword, newPassword, confirmPassword);
            setSuccess(true);
        } catch (err) {
            const message = err.message || '';
            if (message.toLowerCase().includes('current password')) {
                setError('Current password is incorrect');
            } else if (message.toLowerCase().includes('reuse') || message.toLowerCase().includes('recent')) {
                setError('Cannot reuse recent passwords');
            } else {
                setError(message || 'Failed to change password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleContinueToLogin = () => {
        logout();
        navigate('/login', { replace: true });
    };

    if (!email) return null;

    // Success screen
    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                            <ShieldCheck className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Password Changed!</h2>
                        <p className="text-gray-500 mb-6">
                            Please login with your new password to continue.
                        </p>
                        <button
                            onClick={handleContinueToLogin}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                        >
                            Continue to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md relative z-10">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-60 h-48">
                            <KenbrightLogo className="w-full h-full" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">Change Password</h1>
                        <p className="text-gray-500 text-sm">Create a new secure password for your account</p>
                    </div>

                    {/* Info banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700">
                            For your security, you must change your password before accessing the system.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Current Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                                    placeholder="Enter current password"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* New Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                                    placeholder="Create a new password"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Password Requirements</p>
                            <div className="space-y-2">
                                {passwordStrength.map((req) => (
                                    <div
                                        key={req.id}
                                        className={`flex items-center gap-2 text-sm ${
                                            newPassword.length === 0
                                                ? 'text-gray-400'
                                                : req.passed
                                                ? 'text-green-600'
                                                : 'text-red-500'
                                        }`}
                                    >
                                        {newPassword.length === 0 ? (
                                            <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                        ) : req.passed ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span>{req.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-1 transition ${
                                        confirmPassword.length === 0
                                            ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                            : passwordsMatch
                                            ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                                            : 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                    }`}
                                    placeholder="Confirm your new password"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {confirmPassword.length > 0 && !passwordsMatch && (
                                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                    <XCircle className="h-4 w-4" />
                                    Passwords do not match
                                </p>
                            )}
                            {passwordsMatch && (
                                <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                                    <CheckCircle className="h-4 w-4" />
                                    Passwords match
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !isFormValid}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <RefreshCw className="h-5 w-5 animate-spin" />
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <ShieldCheck className="h-5 w-5" />
                                    Change Password
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordPage;
